import React, { useEffect, useLayoutEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Tracker, Url } from "finhelper";
import { NotifyContainer, NotifyManager } from "notify-component";

import { getServer, getUser, handleAuth, logout } from "#app/store/actions/auth";
import { getContacts, handleTitle } from "#app/store/actions/interface";
import { UserContextProvider, useUserContext } from "#app/contexts/User";

import HeaderLanding from "#app/interface/landing/header/Header";
import NotFound from "#app/interface/error/NotFound";
import ErrorBoundary from "#app/interface/error/ErrorBoundary";

import MainPage from "#app/pages/MainPage/MainPage";
import FormOnForbidden from "#app/pages/Auth/Components/FormOnForbidden";

import Layout from "./Layout/Layout";
import CabinetRoutes from "./CabinetRoutes";
import Quiz from "#app/pages/Quiz/Quiz";

interface PagesRouterStateProps {
    auth?: boolean;
    data?: unknown;
    coursesLoading?: boolean;
}

interface PagesRouterDispatchProps {
    getUser: (opts: { callback?: () => void; fallback?: () => void }) => void;
    getServer: () => void;
    handleAuth?: (payload?: unknown) => void;
    logout: () => void;
    handleTitle?: (title: string) => void;
    getContacts: () => void;
}

type PagesRouterProps = PagesRouterStateProps & PagesRouterDispatchProps;

const PagesRouter: React.FC<PagesRouterProps> = (props) => {
    const { auth, getUser, getServer, logout, getContacts } = props;
    const [isActive, setActive] = useState<boolean | undefined>(undefined);
    const navigate = useNavigate();
    const user = useUserContext(props);

    useLayoutEffect(() => {
        Tracker.init();
        getServer();
        getUser({
            callback: () => setActive(true),
            fallback: () => setActive(true),
        });
    }, []);

    useEffect(() => {
        if (
            auth &&
            !Url.getCurrentUrl().includes("cabinet") &&
            !Url.getCurrentUrl().includes("quiz")
        ) {
            navigate("cabinet");
        }
    }, [auth, props]);

    if (typeof isActive === "undefined") {
        return null;
    }

    return (
        <ErrorBoundary>
            <UserContextProvider value={user}>
                <Routes>
                    {auth ? (
                        <Route
                            path="/cabinet/*"
                            element={<CabinetRoutes logout={logout} {...props} />}
                        />
                    ) : (
                        <Route path="/cabinet" element={<Navigate to="/" />} />
                    )}
                    <Route
                        element={
                            <>
                                <HeaderLanding />
                                <Layout />
                            </>
                        }
                    >
                        <Route path="/agreement" element={<MainPage {...props} />} />
                        <Route path="/policy" element={<MainPage {...props} />} />
                        <Route path="/" element={<MainPage {...props} />} />
                        <Route path="/login" element={<FormOnForbidden {...props} />} />
                        <Route path="/quiz" element={<Quiz {...props} />} />
                        <Route path="*" element={<NotFound {...props} />} />
                    </Route>
                </Routes>
                <NotifyContainer>
                    <NotifyManager />
                </NotifyContainer>
            </UserContextProvider>
        </ErrorBoundary>
    );
};

const enhance = connect(
    (state: {
        auth: { auth?: boolean; data?: unknown };
        interfaceComponents: { coursesLoading?: boolean };
    }) => ({
        auth: state.auth.auth,
        data: state.auth.data,
        coursesLoading: state.interfaceComponents.coursesLoading,
    }),
    { getUser, getServer, handleAuth, logout, handleTitle, getContacts }
);

export default enhance(PagesRouter);
